import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import { LOCATION_SLUGS, TYPE_SLUGS } from "../lib/propertySlugs";

function useMeta({ title, description, canonical }) {
  useEffect(() => {
    document.title = title;
    const setMeta = (name, content, attr = "name") => {
      let m = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);
  }, [title, description, canonical]);
}

export default function PropertiesIndex() {
  useMeta({
    title: "Properties for Sale in Karnataka | AcrePulse",
    description: "Discover verified land parcels and residential projects across Bengaluru corridors — Devanahalli, Sarjapur, Whitefield, Hoskote, Bidadi and more. Filter by property type, view legal scorecards, and contact the seller on AcrePulse.",
    canonical: typeof window !== "undefined" ? window.location.href : "",
  });

  return (
    <div className="bg-[#0F172A] text-[#F8FAFC] min-h-screen">
      <nav aria-label="Breadcrumb" className="mx-auto max-w-[1400px] px-6 pt-6 text-xs text-slate-400">
        <ol className="flex items-center gap-2">
          <li><Link to="/" className="hover:text-[#10B981]">Home</Link></li>
          <ChevronRight className="h-3 w-3 text-slate-600" />
          <li className="text-white">Properties</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-[1400px] px-6 pt-6 pb-10 border-b border-[#334155]">
        <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">All Categories</div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter leading-[1.05]">
          Properties for Sale in <span className="text-[#10B981]">Karnataka</span>
        </h1>
        <p className="mt-3 text-slate-400 text-sm max-w-2xl">Verified land parcels and residential projects across Bengaluru's high-growth corridors. Pick a category to see live listings.</p>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-12 space-y-14">
        {Object.entries(LOCATION_SLUGS).map(([locSlug, loc]) => (
          <div key={locSlug}>
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[#10B981]" />
              <h2 className="font-display text-2xl font-semibold tracking-tight">{loc.label}</h2>
              <span className="text-xs text-slate-500 uppercase tracking-[0.2em]">{loc.region}</span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {Object.entries(TYPE_SLUGS).map(([typSlug, typ]) => (
                <Link
                  key={typSlug}
                  to={`/properties/${locSlug}/${typSlug}`}
                  className="text-sm px-4 py-2 rounded-full border border-[#334155] bg-[#1E293B] text-slate-200 hover:border-[#10B981] hover:text-[#10B981] transition-colors"
                >
                  {typ.label} in {loc.label}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
